import { Box, Flex } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import type { PositionVertical } from '@lemonade-hq/ts-helpers';
import parse from 'html-react-parser';
import Image from 'next/image';
import styled from 'styled-components';
import { adjustContentVisibility, Section, SectionTitle, SectionParagraph as Subtitle } from '../Content';
import type { DefaultSectionProps } from '../DefaultSectionProps';
import { GENERIC_PRODUCTS } from './defaults';

export interface BackdropProps {
  readonly show?: boolean;
  readonly height?: number;
  readonly bgColor?: string;
  readonly position?: PositionVertical;
}

const Title = styled(SectionTitle)`
  ${MEDIA_QUERIES.tabletAndDesktop`
    white-space: initial;
  `}
`;

const ContentWrapper = styled(Flex)<{ readonly bgImage: string }>`
  position: relative;
  flex-direction: column;
  align-items: center;
  height: 580px;
  z-index: 1;
  background: transparent url(${({ bgImage }) => bgImage}) 71% 100% no-repeat;
  padding-inline: 20px;

  @media (max-width: 425px) {
    background-size: 1273px 333px;
    height: 645px;
    bottom: 0;
    background-position: 74% 100%;
  }

  @media (min-width: 426px) and (max-width: 768px) {
    background-size: 1347px 317px;
  }

  ${MEDIA_QUERIES.tablet`
        background-size: 1250px 311px;
        height: 762px;
        background-size: contain;
        background: transparent url(${({ bgImage }) => bgImage}) bottom center no-repeat;
        background-position-x: 71%;
    `}

  ${MEDIA_QUERIES.desktop`
        background-size: 1250px 311px;
        height: 700px;
        background-size: contain;
        background: transparent url(${({ bgImage }) => bgImage}) bottom center no-repeat;
    `}

    @media (min-width: 1676px) {
    background-size: 1671px;
  }
`;

const Backdrop = styled(Box).attrs({
  'data-testid': 'backdrop',
})<{ readonly backdrop: BackdropProps }>`
  position: absolute;
  width: 100%;
  ${({ backdrop }) => backdrop.position}: 0;
  height: ${({ backdrop }) => backdrop.height}px;
  background: ${({ backdrop }) => backdrop.bgColor};
  z-index: 0;
  left: 0;
`;

const StyledSection = styled(Section).attrs({
  pb: ['0px'],
  px: ['0px', '0px', '0px'],
})`
  ${adjustContentVisibility}
`;

const ProductLogo = styled(Image)`
  position: relative;
  display: block;
  margin: 30px auto 0 auto;
`;

export interface GenericProductsProps extends DefaultSectionProps {
  readonly title: string;
  readonly subtitle: string;
  readonly bgImage: string | StaticImageData;
  readonly backdrop?: BackdropProps;
  readonly productLogo?: {
    readonly img: StaticImageData | string;
    readonly alt: string;
    readonly height: number;
    readonly width: number;
  };
}
export const GenericProducts: React.FC<GenericProductsProps> = ({
  title,
  subtitle,
  bgImage,
  backdrop = GENERIC_PRODUCTS.backdrop,
  productLogo,
  ...sectionProps
}) => {
  return (
    <StyledSection {...sectionProps} position="relative" pt={[40, 70]}>
      <Box width="100%">
        <ContentWrapper bgImage={bgImage}>
          <Title data-edit-for="title">{parse(title)}</Title>
          <Box mt={'10px'}>
            <Subtitle data-edit-for="subtitle">{parse(subtitle)}</Subtitle>
          </Box>
          {productLogo != null && (
            <ProductLogo src={productLogo.img} height={productLogo.height} width={productLogo.width} alt="" />
          )}
        </ContentWrapper>
        {backdrop?.show && <Backdrop backdrop={backdrop} />}
      </Box>
    </StyledSection>
  );
};
